import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import jwt_decode from 'jwt-decode'
import ModalActions from 'reducers/Modal'
import PlayQueueActions, { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import ArchivedActions from 'reducers/Archived'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import NotificationActions from 'reducers/Notification'
import FlaggedActions, { thunks as FlaggedThunks } from 'reducers/Flagged'
import { NotificationType } from 'constants/enums'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { trackEvent } from 'utils/segmentService'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const useQuickViewModalActions = ({
  dispatch: providedDispatch,
  release,
  setIsContactModalOpen
}) => {
  const dispatch = providedDispatch || useDispatch()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()

  // One-shot guards so a release can only be played / enqueued once per modal
  // open, matching the legacy ReleaseModal and preventing spammed inserts.
  const [didTapPlay, setDidTapPlay] = useState(false)
  const [didTapPlayNext, setDidTapPlayNext] = useState(false)
  const [didTapPlayLater, setDidTapPlayLater] = useState(false)

  useEffect(() => {
    setDidTapPlay(false)
    setDidTapPlayNext(false)
    setDidTapPlayLater(false)
  }, [release?.releaseid])

  const syncReleaseState = useCallback(
    (updatedRelease) => {
      dispatch(ReleaseActions.toggleReleaseInModal(updatedRelease))
      dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
      dispatch(ReleaseActions.updateRelease(updatedRelease))
    },
    [dispatch]
  )

  const canShareRelease = (() => {
    try {
      const token = localStorage.getItem('accessToken')
      const currentUser = jwt_decode(token)
      return Boolean(currentUser?.verified && release?.allow_sharing)
    } catch (error) {
      return false
    }
  })()

  const handleDownloadRelease = useCallback(
    (format) => {
      trackEvent('Click', {
        source: 'release_modal',
        type: 'download_all',
        release_id: String(release.releaseid),
        artist: release.artist || '',
        title: release.title || '',
        format,
        track_count: String(release.songs?.length || 0)
      })
      dispatch(
        DownloadsThunks.addReleaseToDownloadQueue(
          release,
          format,
          'release_modal'
        )
      )
    },
    [dispatch, release]
  )

  const handleDownloadSong = useCallback(
    (song, format = '') => {
      trackEvent('Click', {
        source: 'release_modal',
        type: 'download_song',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        format: format || 'video',
        ordinal: String((song.ordinal || 0) + 1 || '')
      })

      if (song.assettype === 'video') {
        dispatch(
          DownloadsThunks.addVideoToDownloadQueue(
            song,
            'release_modal_song_list'
          )
        )
        return
      }

      dispatch(
        DownloadsThunks.addSongToDownloadQueue(song, {
          format,
          source: 'release_modal_song_list'
        })
      )
    },
    [dispatch]
  )

  const handlePlayRelease = useCallback(() => {
    if (didTapPlay) return
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: PLAY_SOURCES.RELEASE_MODAL,
        trigger: PLAY_TRIGGERS.HEADER_MENU
      })
    )
    trackEvent('Click', {
      source: 'release_modal',
      type: 'play',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      track_count: String(release.songs?.length || 0)
    })
    setDidTapPlay(true)
  }, [dispatch, release, didTapPlay])

  const handlePlaySong = useCallback(
    (song, index, trigger = PLAY_TRIGGERS.ROW) => {
      dispatch(
        PlayQueueThunks.playSong(song, {
          source: PLAY_SOURCES.RELEASE_MODAL,
          trigger
        })
      )
      trackEvent('Click', {
        source: 'release_modal',
        type: 'play_song',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        ordinal: String(index + 1)
      })
    },
    [dispatch]
  )

  const handleReleasePlayNext = useCallback(() => {
    if (didTapPlayNext) return
    dispatch(PlayQueueThunks.insertReleaseNext(release.releaseid))
    trackEvent('Click', {
      source: 'release_modal',
      type: 'play_next',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      item_type: 'release',
      track_count: String(release.songs?.length || 0)
    })
    setDidTapPlayNext(true)
  }, [dispatch, release, didTapPlayNext])

  const handleReleasePlayLater = useCallback(() => {
    if (didTapPlayLater) return
    dispatch(PlayQueueThunks.insertReleaseAtBottom(release.releaseid))
    trackEvent('Click', {
      source: 'release_modal',
      type: 'play_later',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      item_type: 'release',
      track_count: String(release.songs?.length || 0)
    })
    setDidTapPlayLater(true)
  }, [dispatch, release, didTapPlayLater])

  const handleTrackPlayNext = useCallback(
    (song) => {
      dispatch(PlayQueueActions.insertItemNext(song))
      trackEvent('Click', {
        source: 'release_modal',
        type: 'play_next',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        item_type: 'song'
      })
    },
    [dispatch]
  )

  const handleTrackPlayLater = useCallback(
    (song) => {
      dispatch(PlayQueueActions.insertItemAtBottom(song))
      trackEvent('Click', {
        source: 'release_modal',
        type: 'play_later',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        item_type: 'song'
      })
    },
    [dispatch]
  )

  const handleFlagRelease = useCallback(() => {
    const nextFlagged = !release?.flagged

    dispatch(
      FlaggedThunks.flag(
        release.releaseid,
        nextFlagged,
        () => {
          const updatedRelease = {
            ...release,
            flagged: nextFlagged,
            songs: release.songs?.map((song) => ({
              ...song,
              flagged: nextFlagged
            }))
          }

          syncReleaseState(updatedRelease)

          if (!release?.flagged) {
            dispatch(FlaggedActions.addFlaggedRelease(updatedRelease))
          } else {
            dispatch(FlaggedActions.removeFlaggedReleaseId(release.releaseid))
          }

          dispatch(
            NotificationActions.broadcastMessage(
              `${release.artist} - ${release.title}`,
              nextFlagged ? NotificationType.flag : NotificationType.unFlag
            )
          )

          trackEvent('Click', {
            source: 'release_modal',
            type: 'flag',
            release_id: String(release.releaseid),
            artist: release.artist || '',
            title: release.title || '',
            action: nextFlagged ? 'flag' : 'unflag'
          })
        },
        () => {}
      )
    )
  }, [dispatch, release, syncReleaseState])

  const handleArchiveRelease = useCallback(() => {
    const currentValue = Boolean(release?.archived)
    const currentFlagged = Boolean(release?.flagged)

    dispatch(
      ReleaseThunks.archive(
        release.releaseid,
        !currentValue,
        () => {
          const shouldUnflagOnArchive = !currentValue && currentFlagged
          const updatedRelease = {
            ...release,
            archived: !currentValue,
            flagged: shouldUnflagOnArchive ? false : release.flagged,
            songs: shouldUnflagOnArchive
              ? release.songs?.map((song) => ({
                  ...song,
                  flagged: false
                }))
              : release.songs
          }

          syncReleaseState(updatedRelease)

          if (archiveToFilterEnabled) {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
          } else if (!currentValue) {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
            dispatch(ArchivedActions.addArchivedRelease(updatedRelease))
          } else {
            dispatch(ReleaseActions.addRelease(updatedRelease))
            dispatch(ArchivedActions.removeArchivedRelease(updatedRelease))
          }

          dispatch(
            NotificationActions.broadcastMessage(
              `${release.artist} - ${release.title}`,
              currentValue
                ? NotificationType.unArchive
                : NotificationType.archive
            )
          )

          trackEvent('Click', {
            source: 'release_modal',
            type: 'archive',
            release_id: String(release.releaseid),
            artist: release.artist || '',
            title: release.title || '',
            action: currentValue ? 'restore' : 'archive'
          })

          if (shouldUnflagOnArchive) {
            dispatch(
              FlaggedThunks.flag(release.releaseid, false, () => {
                dispatch(
                  FlaggedActions.removeFlaggedReleaseId(release.releaseid)
                )
              })
            )
          }
        },
        () => {},
        archiveToFilterEnabled
      )
    )
  }, [dispatch, release, syncReleaseState, archiveToFilterEnabled])

  const handleAddReleaseToPlaylist = useCallback(
    (playlist) => {
      if (archiveToFilterEnabled && release?.archived) return
      const trackIds = (release?.songs || [])
        .filter((song) => song.assettype === 'audio')
        .map((song) => song.trackid)

      trackEvent('Click', {
        source: 'release_modal',
        type: 'add_to_playlist',
        release_id: String(release.releaseid),
        artist: release.artist || '',
        title: release.title || '',
        playlist_id: String(playlist.playlistid),
        playlist_name: playlist.name || '',
        track_count: String(trackIds.length)
      })

      dispatch(
        PlaylistThunks.addTracks(playlist.playlistid, trackIds, () =>
          dispatch(
            NotificationActions.broadcastMessage(
              {
                songTitle: `${release.artist} - ${release.title}`,
                playlistTitle: playlist.name
              },
              NotificationType.addedToPlaylist
            )
          )
        )
      )
    },
    [dispatch, release, archiveToFilterEnabled]
  )

  const handleAddTrackToPlaylist = useCallback(
    (playlist, song, index) => {
      trackEvent('Click', {
        source: 'release_modal',
        type: 'add_to_playlist',
        id: String(song.trackid),
        artist: song.artist || '',
        title: song.title || '',
        playlist_id: String(playlist.playlistid),
        playlist_name: playlist.name || '',
        track_count: '1',
        ordinal: String(index + 1)
      })

      dispatch(
        PlaylistThunks.addTracks(playlist.playlistid, [song.trackid], () =>
          dispatch(
            NotificationActions.broadcastMessage(
              {
                songTitle: `${song.artist} - ${song.title}`,
                playlistTitle: playlist.name
              },
              NotificationType.addedToPlaylist
            )
          )
        )
      )
    },
    [dispatch]
  )

  const createNewReleasePlaylist = useCallback(() => {
    if (archiveToFilterEnabled && release?.archived) return
    const trackIds = (release?.songs || [])
      .filter((song) => song.assettype === 'audio')
      .map((song) => song.trackid)
    dispatch(PlaylistActions.setSongsToAdd(trackIds))
    dispatch(ModalActions.openPlaylistModal('release_quick_view'))
  }, [dispatch, release, archiveToFilterEnabled])

  const createNewTrackPlaylist = useCallback(
    (song) => {
      dispatch(PlaylistActions.setSongsToAdd([song.trackid]))
      dispatch(ModalActions.openPlaylistModal('release_quick_view'))
    },
    [dispatch]
  )

  const openShareReleaseModal = useCallback(() => {
    dispatch(ModalActions.toggleShareReleaseModal())
  }, [dispatch])

  const openContactModal = useCallback(() => {
    if (setIsContactModalOpen) {
      setIsContactModalOpen(true)
    }
  }, [setIsContactModalOpen])

  return {
    canShareRelease,
    didTapPlay,
    didTapPlayNext,
    didTapPlayLater,
    handleDownloadRelease,
    handleDownloadSong,
    handlePlayRelease,
    handlePlaySong,
    handleReleasePlayNext,
    handleReleasePlayLater,
    handleTrackPlayNext,
    handleTrackPlayLater,
    handleFlagRelease,
    handleArchiveRelease,
    handleAddReleaseToPlaylist,
    handleAddTrackToPlaylist,
    createNewReleasePlaylist,
    createNewTrackPlaylist,
    openShareReleaseModal,
    openContactModal
  }
}

export default useQuickViewModalActions
